import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import CommentItem from "@/pages/home/components/CommentItem";
import { comments, discussionMeta } from "@/mocks/homeData";

export default function Discussion() {
  const [draft, setDraft] = useState("");
  const { message, show } = useToast();

  const stats = [
    { label: "参与讨论", value: discussionMeta.participants, icon: "ri-group-line" },
    { label: "讨论串", value: discussionMeta.threads, icon: "ri-discuss-line" },
    { label: "今日新增", value: "47", icon: "ri-fire-line" },
  ];

  const handleSubmit = () => {
    if (!draft.trim()) {
      show("先写点什么再发送吧");
      return;
    }
    show("当前为静态展示，评论暂不会真正提交");
  };

  return (
    <section id="discussion" className="w-full border-b border-background-200 bg-secondary-50">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <Reveal>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.22em] text-accent-600">
                <span className="h-px w-6 bg-accent-400" />
                DISCUSSION
              </span>
              <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
                讨论区：争议的地方，先摆出来
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                人物关系最容易各说各话。这里把讨论热度最高的几条放在前面，补充线索、指出矛盾都欢迎。
              </p>
            </div>

            <dl className="grid w-full grid-cols-3 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:w-[380px] lg:flex-none">
              {stats.map((item) => (
                <div key={item.label} className="flex flex-col gap-1 bg-background-50 px-3 py-4 md:px-4">
                  <dt className="flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
                    <i className={`${item.icon} text-sm text-primary-500`} />
                    {item.label}
                  </dt>
                  <dd className="font-heading text-xl font-bold text-foreground-950">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-10 flex flex-col gap-3 rounded-lg border border-accent-200 bg-accent-50 p-5 md:flex-row md:items-center md:justify-between md:gap-6">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-flashlight-line text-sm" />
              </span>
              <div>
                <span className="font-label text-[11px] tracking-wide text-accent-700">今日热帖</span>
                <p className="mt-1 font-heading text-base font-bold text-foreground-950">
                  {discussionMeta.hottest}
                </p>
              </div>
            </div>
            <span className="whitespace-nowrap font-label text-xs text-accent-700">已有 326 条回复</span>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 md:px-6">
              {comments.map((item) => (
                <CommentItem
                  key={item.id}
                  name={item.name}
                  time={item.time}
                  initial={item.initial}
                  text={item.text}
                  likes={item.likes}
                  replies={item.replies}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => show("已加载全部演示评论")}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 py-3 font-label text-sm text-foreground-700 transition-colors duration-200 hover:border-foreground-300 hover:text-foreground-950"
            >
              加载更多讨论
              <i className="ri-arrow-down-line text-base" />
            </button>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <h4 className="font-heading text-base font-bold text-foreground-950">参与讨论</h4>
              <p className="mt-1.5 font-label text-xs text-foreground-500">
                补充线索时，请尽量写清时间和信息来源。
              </p>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                maxLength={500}
                rows={5}
                placeholder="写下你的补充或疑问…"
                className="mt-4 w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 font-label text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
              />
              <div className="mt-1.5 flex items-center justify-between font-label text-[11px] text-foreground-400">
                <span>演示环境 · 不会真实提交</span>
                <span>{draft.length}/500</span>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-2.5 font-label text-sm text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                发布讨论
              </button>
              <div className="mt-4 flex items-start gap-2 border-t border-background-200 pt-4 font-label text-[11px] leading-relaxed text-foreground-500">
                <i className="ri-shield-check-line mt-0.5 text-sm text-secondary-500" />
                讨论内容仅作人物脉络补充，请勿涉及个人隐私信息。
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast message={message} />
    </section>
  );
}